import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DisplayMode } from '../../models/project';
import { IconArrow, IconDesktop, IconPhone } from '../Icons';
import { landingMedia } from './media';

interface HeroSectionProps {
  pitch: string;
  onPitchChange: (value: string) => void;
  displayMode: DisplayMode;
  onDisplayModeChange: (mode: DisplayMode) => void;
  onCreate: () => void;
}

export default function HeroSection({ pitch, onPitchChange, displayMode, onDisplayModeChange, onCreate }: HeroSectionProps) {
  const { t } = useTranslation('landing');
  const [videoIndex, setVideoIndex] = useState(0);
  const [failedVideos, setFailedVideos] = useState(0);
  const videos = landingMedia.heroVideos;
  const isMobileMode = displayMode !== 'PC';

  const nextVideo = () => setVideoIndex((i) => (i + 1) % videos.length);

  return (
    <section
      style={{
        position: 'relative',
        minHeight: 'calc(100svh - var(--header-h))',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'clamp(40px, 12svh, 96px) var(--gutter) clamp(32px, 9svh, 72px)',
        overflow: 'hidden',
        background: 'var(--bg)',
      }}
    >
      {failedVideos < videos.length && (
        <video
          key={videos[videoIndex]}
          src={videos[videoIndex]}
          autoPlay
          muted
          playsInline
          preload="auto"
          onEnded={nextVideo}
          onError={() => {
            setFailedVideos((n) => n + 1);
            nextVideo();
          }}
          className="media-fill"
        />
      )}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'var(--scrim-media)' }} />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background:
            'radial-gradient(70% 60% at 50% 45%, rgba(5,6,8,0.15), rgba(5,6,8,0.7) 100%), linear-gradient(180deg, rgba(5,6,8,0.55) 0%, rgba(5,6,8,0) 30%, rgba(5,6,8,0) 65%, var(--bg) 100%)',
        }}
      />
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: 980,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <h1 className="t-hero" style={{ fontSize: 'min(var(--fs-hero), 11svh)', margin: '0 0 22px' }}>
          {t('hero.titleA', 'Turn your game idea')}
          <br />
          <span className="t-accent">{t('hero.titleB', 'into a game business.')}</span>
        </h1>
        <p
          style={{
            fontSize: 'var(--fs-hero-lede)',
            lineHeight: 1.6,
            color: 'var(--text-soft)',
            maxWidth: 640,
            margin: '0 0 30px',
            textWrap: 'pretty',
          }}
        >
          {t(
            'hero.lede',
            'Describe your idea. Entire builds the game, gets it ready for mobile and PC, and gives you everything you need to launch, monetize, understand your players, and keep improving.',
          )}
        </p>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 12,
            marginBottom: 'clamp(20px, 7svh, 80px)',
          }}
        >
          <button type="button" className="btn primary lg" onClick={onCreate}>
            {t('cta.startFree', 'Start Free')}
          </button>
          <a href="#create" className="btn glass lg">
            {t('hero.seeMore', 'See more')}
          </a>
        </div>
        <form
          className="prompt"
          style={{ maxWidth: 860 }}
          onSubmit={(e) => {
            e.preventDefault();
            onCreate();
          }}
        >
          <textarea
            rows={2}
            value={pitch}
            onChange={(e) => onPitchChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                onCreate();
              }
            }}
            placeholder={t('hero.placeholder', 'Describe your game...')}
            aria-label={t('hero.placeholder', 'Describe your game...')}
          />
          <div className="prompt-bar">
            <div className="seg" role="radiogroup" aria-label={t('hero.platform', 'Platform')}>
              <button
                type="button"
                role="radio"
                aria-checked={isMobileMode}
                className={'seg-item' + (isMobileMode ? ' on' : '')}
                onClick={() => onDisplayModeChange('MobilePortrait')}
              >
                <IconPhone size={14} />
                {t('hero.mobile', 'Mobile')}
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={!isMobileMode}
                className={'seg-item' + (!isMobileMode ? ' on' : '')}
                onClick={() => onDisplayModeChange('PC')}
              >
                <IconDesktop size={14} />
                {t('hero.pc', 'PC')}
              </button>
            </div>
            <button type="submit" className="btn primary" style={{ padding: '0 20px', fontSize: 15 }}>
              {t('hero.create', 'Create game')}
              <IconArrow size={15} />
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
